import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { AlertTriangle, ArrowLeft, CheckCircle2, Eye, Pencil, Plus, Trash2 } from "lucide-react";
import { PageHeader } from "../../components/layout/PageHeader";
import { ConfirmModal } from "../../components/ui/ConfirmModal";
import { portalReportsAdminApi, type AdminReport } from "../../api/portalReportsAdminApi";
import { customersAdminApi, type AdminCustomerListItem } from "../../api/customersAdminApi";
import type { ReportAudience, ReportDefinition, ReportInput, ReportOption, ReportSection } from "../../api/portalReportsApi";
import { SectionEditor } from "../../components/admin/reports/SectionEditor";
import { DynamicReport } from "../../components/reports/dynamic/DynamicReport";
import { HttpError } from "../../api/http";

const inputStyle: React.CSSProperties = { width: "100%", padding: "8px 10px", border: "2px solid var(--portal-border)", borderRadius: 8, fontSize: "0.9rem", fontFamily: "inherit", background: "white" };
const smallInput: React.CSSProperties = { ...inputStyle, padding: "6px 8px", fontSize: "0.85rem", borderWidth: 1 };
const fieldGrid: React.CSSProperties = { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 12 };
const listRow: React.CSSProperties = { display: "flex", alignItems: "center", gap: 12, padding: "10px 2px", borderTop: "1px solid var(--portal-border)" };
const thStyle: React.CSSProperties = { textAlign: "left", padding: "6px 8px", borderBottom: "1px solid var(--portal-border)", color: "var(--portal-muted)", fontSize: "0.72rem", textTransform: "uppercase", letterSpacing: "0.04em", whiteSpace: "nowrap" };
const tdStyle: React.CSSProperties = { padding: "4px 8px", borderBottom: "1px solid var(--portal-border)", verticalAlign: "middle" };

function FieldLabel({ children }: { children: React.ReactNode }) {
  return <span style={{ fontSize: "0.85rem", color: "var(--portal-muted)" }}>{children}</span>;
}

function Status({ error, success }: { error: string | null; success: string | null }) {
  if (!error && !success) return null;
  return (
    <div style={{ display: "flex", gap: 8, alignItems: "center", color: error ? "#c0392b" : "var(--portal-status-success-darker)", fontSize: "0.85rem" }}>
      {error ? <AlertTriangle size={16} /> : <CheckCircle2 size={16} />}
      <span>{error ?? success}</span>
    </div>
  );
}

const VALIDATION_TEXT: Record<string, string> = {
  sections_required: "Add at least one section before enabling the report.",
  section_needs_customer_scope: "Every section of a customer report needs a parameter with source “Session customer”",
  section_needs_manager_scope: "Every section of a manager report needs a parameter with source “Session manager”",
  param_input_missing: "A parameter refers to an input that does not exist",
  procedure_not_allowed: "Procedure not allowed",
  procedure_missing: "Procedure does not exist",
  name_taken: "Another report already has this name",
  name_invalid: "The name is the link, so it cannot contain / ? # or %",
  chart_category_unknown: "Chart category column is unknown",
  chart_value_required: "Chart needs a value column",
};

function describeError(err: unknown): string {
  if (err instanceof HttpError && err.data && typeof err.data === "object" && "details" in (err.data as object)) {
    const details = (err.data as { details?: string[] }).details ?? [];
    return details.map((d) => {
      const [code, rest] = d.split(":");
      return (VALIDATION_TEXT[code] ?? code) + (rest ? ` (${rest})` : "");
    }).join(" · ") || "The definition is invalid.";
  }
  if (err instanceof HttpError && VALIDATION_TEXT[err.message]) return VALIDATION_TEXT[err.message];
  return err instanceof Error ? err.message : "Failed";
}

/**
 * Admin report builder for one report: general settings, customer assignment,
 * inputs, sections (each backed by a stored procedure) and a live preview.
 * The definition is edited in page state and saved as one JSON document.
 */
export function ReportEditPage() {
  const { reportName: param } = useParams<{ reportName: string }>();
  const reportName = param ?? "";
  const navigate = useNavigate();
  const [report, setReport] = useState<AdminReport | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  // editable copy
  const [name, setName] = useState("");
  const [menuLabel, setMenuLabel] = useState("");
  const [description, setDescription] = useState("");
  const [enabled, setEnabled] = useState(false);
  const [audience, setAudience] = useState<ReportAudience>("customer");
  const [sortOrder, setSortOrder] = useState(100);
  const [definition, setDefinition] = useState<ReportDefinition>({ version: 1, inputs: [], sections: [] });
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveOk, setSaveOk] = useState<string | null>(null);
  const [deleteOpen, setDeleteOpen] = useState(false);

  const [editing, setEditing] = useState<{ open: boolean; section: ReportSection | null }>({ open: false, section: null });

  useEffect(() => {
    let active = true;
    portalReportsAdminApi
      .get(reportName)
      .then((r) => {
        if (!active) return;
        setReport(r);
        setName(r.name);
        setMenuLabel(r.menuLabel);
        setDescription(r.description);
        setEnabled(r.enabled);
        setAudience(r.audience);
        setSortOrder(r.sortOrder);
        setDefinition({ ...r.definition, version: r.definition.version ?? 1, inputs: r.definition.inputs ?? [], sections: r.definition.sections ?? [] });
        setDirty(false);
      })
      .catch((err) => active && setLoadError(err instanceof Error ? err.message : "Failed to load the report"));
    return () => {
      active = false;
    };
  }, [reportName]);

  const touch = () => {
    setDirty(true);
    setSaveOk(null);
  };
  const updateDefinition = (fn: (d: ReportDefinition) => ReportDefinition) => {
    setDefinition((d) => fn(d));
    touch();
  };

  const save = async (enable?: boolean) => {
    setSaving(true);
    setSaveError(null);
    setSaveOk(null);
    const nextEnabled = enable ?? enabled;
    try {
      const res = await portalReportsAdminApi.update(reportName, { name, menuLabel, description, enabled: nextEnabled, audience, sortOrder, definition });
      setEnabled(nextEnabled);
      setDirty(false);
      setSaveOk(res.warnings.length > 0 ? `Saved (draft has ${res.warnings.length} issue${res.warnings.length === 1 ? "" : "s"}; fix them before enabling)` : "Saved");
      // The name is the permalink: follow a rename.
      if (res.name && res.name !== reportName) navigate(`/admin/reports/${encodeURIComponent(res.name)}`, { replace: true });
    } catch (err) {
      setSaveError(describeError(err));
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    try {
      await portalReportsAdminApi.remove(reportName);
      navigate("/admin/reports");
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : "Failed to delete");
      setDeleteOpen(false);
    }
  };

  /** Inputs referenced by any section parameter (others are unused and can be removed). */
  const usedInputKeys = useMemo(() => {
    const keys = new Set<string>();
    for (const s of definition.sections) for (const p of s.params ?? []) if (p.source === "input" && p.inputKey) keys.add(p.inputKey.toLowerCase());
    return keys;
  }, [definition.sections]);

  const onSectionSaved = (section: ReportSection, newInputs: ReportInput[]) => {
    updateDefinition((d) => {
      const exists = d.sections.some((s) => s.id === section.id);
      return {
        ...d,
        inputs: [...d.inputs, ...newInputs.filter((n) => !d.inputs.some((i) => i.key.toLowerCase() === n.key.toLowerCase()))],
        sections: exists ? d.sections.map((s) => (s.id === section.id ? section : s)) : [...d.sections, section],
      };
    });
    setEditing({ open: false, section: null });
  };

  const moveSection = (id: string, dir: -1 | 1) =>
    updateDefinition((d) => {
      const i = d.sections.findIndex((s) => s.id === id);
      const j = i + dir;
      if (i < 0 || j < 0 || j >= d.sections.length) return d;
      const next = [...d.sections];
      [next[i], next[j]] = [next[j], next[i]];
      return { ...d, sections: next };
    });

  return (
    <>
      <PageHeader
        title={report ? report.name : "Report"}
        actions={
          <Link to="/admin/reports" className="btn btn-secondary" style={{ display: "inline-flex", alignItems: "center", gap: 6, textDecoration: "none" }}>
            <ArrowLeft size={14} /> Back to reports
          </Link>
        }
      />
      <div className="dashboard" style={{ display: "grid", gap: 16 }}>
        {loadError && <div className="tile" style={{ borderColor: "#e74c3c", color: "#c0392b", background: "#fdecea" }}>{loadError}</div>}
        {!report && !loadError && <div className="tile" style={{ color: "var(--portal-muted)" }}>Loading…</div>}
        {report && (
          <>
            {/* Sticky save bar */}
            <div className="tile" style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap", position: "sticky", top: 8, zIndex: 5 }}>
              <span style={{ fontSize: "0.9rem" }}>
                {enabled ? <span style={{ color: "var(--portal-status-success-darker)", fontWeight: 700 }}>Enabled</span> : <span style={{ color: "#8a5a00", fontWeight: 700 }}>Disabled</span>}
                {dirty && <span style={{ color: "var(--portal-muted)" }}> · unsaved changes</span>}
              </span>
              <Status error={saveError} success={saveOk} />
              <span style={{ marginLeft: "auto", display: "inline-flex", gap: 8 }}>
                {enabled ? (
                  <button type="button" className="btn btn-secondary" onClick={() => void save(false)} disabled={saving}>Disable</button>
                ) : (
                  <button type="button" className="btn btn-secondary" onClick={() => void save(true)} disabled={saving}>Save & enable</button>
                )}
                <button type="button" className="btn btn-primary" onClick={() => void save()} disabled={saving}>{saving ? "Saving…" : "Save"}</button>
              </span>
            </div>

            <section className="tile" style={{ display: "grid", gap: 12 }}>
              <header className="tile-head"><h3>General</h3></header>
              <div style={fieldGrid}>
                <label style={{ display: "grid", gap: 4 }}><FieldLabel>Name (also the link: /reports/custom/&lt;name&gt;)</FieldLabel><input className="portal-input-inline" style={inputStyle} value={name} onChange={(e) => { setName(e.target.value); touch(); }} /></label>
                <label style={{ display: "grid", gap: 4 }}><FieldLabel>Menu label</FieldLabel><input className="portal-input-inline" style={inputStyle} value={menuLabel} maxLength={50} onChange={(e) => { setMenuLabel(e.target.value); touch(); }} /></label>
                <label style={{ display: "grid", gap: 4 }}>
                  <FieldLabel>Audience</FieldLabel>
                  <select className="portal-input-inline" style={inputStyle} value={audience} onChange={(e) => { setAudience(e.target.value as ReportAudience); touch(); }}>
                    <option value="customer">Customers (assigned)</option>
                    <option value="manager">Managers</option>
                    <option value="admin">Admin only</option>
                  </select>
                </label>
                <label style={{ display: "grid", gap: 4 }} title="Below 100: before the built-in reports (Inbound, Outbound, …) in the menu; 100 and up: after them."><FieldLabel>Sort order</FieldLabel><input className="portal-input-inline" style={inputStyle} type="number" value={sortOrder} onChange={(e) => { setSortOrder(Number(e.target.value) || 0); touch(); }} /></label>
              </div>
              <label style={{ display: "grid", gap: 4 }}><FieldLabel>Description (shown above the report)</FieldLabel><input className="portal-input-inline" style={inputStyle} value={description} maxLength={500} onChange={(e) => { setDescription(e.target.value); touch(); }} /></label>
              <label style={{ display: "inline-flex", alignItems: "center", gap: 8, fontSize: "0.9rem" }}>
                <input type="checkbox" checked={!!definition.autoRun} onChange={(e) => updateDefinition((d) => ({ ...d, autoRun: e.target.checked }))} />
                Run automatically when opened (inputs use their defaults)
              </label>
            </section>

            {audience === "customer" && <AssignmentSection report={report} />}

            <section className="tile" style={{ display: "grid", gap: 12 }}>
              <header className="tile-head" style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <h3>Sections</h3>
                <button type="button" className="btn btn-primary" onClick={() => setEditing({ open: true, section: null })} style={{ marginLeft: "auto", display: "inline-flex", alignItems: "center", gap: 6 }}>
                  <Plus size={14} /> Add section
                </button>
              </header>
              {definition.sections.length === 0 ? (
                <div style={{ color: "var(--portal-muted)", fontSize: "0.9rem" }}>No sections yet. A section is a table, stat tiles or a chart on top of one stored procedure.</div>
              ) : (
                <div>
                  {definition.sections.map((s, i) => (
                    <div key={s.id} style={listRow}>
                      <span style={{ display: "grid", gap: 2, minWidth: 0, flex: 1 }}>
                        <span style={{ fontWeight: 700 }}>{s.title || s.id} <span style={{ fontWeight: 400, color: "var(--portal-muted)" }}>· {s.kind}</span></span>
                        <span style={{ fontSize: "0.85rem", color: "var(--portal-muted)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          dbo.{s.procedure} · {s.columns.filter((c) => c.visible !== false).length}/{s.columns.length} columns
                          {(s.filterColumns?.length ?? 0) > 0 && <> · filters: {s.filterColumns!.join(", ")}</>}
                        </span>
                      </span>
                      <button type="button" className="btn btn-secondary" onClick={() => moveSection(s.id, -1)} disabled={i === 0} style={{ padding: "4px 8px" }} aria-label="Move up">↑</button>
                      <button type="button" className="btn btn-secondary" onClick={() => moveSection(s.id, 1)} disabled={i === definition.sections.length - 1} style={{ padding: "4px 8px" }} aria-label="Move down">↓</button>
                      <button type="button" className="btn btn-secondary" onClick={() => setEditing({ open: true, section: s })} style={{ display: "inline-flex", alignItems: "center", gap: 6 }}><Pencil size={14} /> Edit</button>
                      <button type="button" className="btn btn-outline-danger" onClick={() => updateDefinition((d) => ({ ...d, sections: d.sections.filter((x) => x.id !== s.id) }))} style={{ display: "inline-flex", alignItems: "center", gap: 6 }}><Trash2 size={14} /></button>
                    </div>
                  ))}
                </div>
              )}
            </section>

            <InputsSection definition={definition} usedInputKeys={usedInputKeys} onChange={updateDefinition} />

            <PreviewSection reportName={report.name} definition={definition} audience={audience} dirty={dirty} />

            <section className="tile" style={{ display: "grid", gap: 8 }}>
              <header className="tile-head"><h3>Danger zone</h3></header>
              <div>
                <button type="button" className="btn btn-outline-danger" onClick={() => setDeleteOpen(true)}>Delete report</button>
              </div>
            </section>
          </>
        )}
      </div>

      <SectionEditor
        open={editing.open}
        section={editing.section}
        inputs={definition.inputs}
        audience={audience}
        existingIds={definition.sections.map((s) => s.id)}
        onSave={onSectionSaved}
        onClose={() => setEditing({ open: false, section: null })}
      />
      <ConfirmModal open={deleteOpen} onClose={() => setDeleteOpen(false)} title="Delete report" onConfirm={() => void remove()}>
        Delete “{report?.name}”? Customers lose access immediately. The stored procedures are not touched.
      </ConfirmModal>
    </>
  );
}

// ---- assignment ---------------------------------------------------------------

function AssignmentSection({ report }: { report: AdminReport }) {
  const [all, setAll] = useState(report.assignedAll);
  const [ids, setIds] = useState<number[]>(report.customerIds);
  const [customers, setCustomers] = useState<AdminCustomerListItem[]>([]);
  const [filter, setFilter] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => {
    customersAdminApi.list().then((r) => setCustomers(r.items)).catch(() => setCustomers([]));
  }, []);

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    return customers
      .filter((c) => !q || c.customerName.toLowerCase().includes(q) || c.managerName.toLowerCase().includes(q))
      .sort((a, b) => Number(ids.includes(b.customerId)) - Number(ids.includes(a.customerId)) || a.customerName.localeCompare(b.customerName));
  }, [customers, filter, ids]);

  const save = async () => {
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      await portalReportsAdminApi.setAssignments(report.name, { allCustomers: all, customerIds: all ? [] : ids });
      setSuccess("Assignment saved");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="tile" style={{ display: "grid", gap: 12 }}>
      <header className="tile-head"><h3>Customers</h3></header>
      <div style={{ display: "flex", gap: 16, fontSize: "0.9rem", flexWrap: "wrap" }}>
        <label style={{ display: "inline-flex", alignItems: "center", gap: 6 }}><input type="radio" checked={all} onChange={() => setAll(true)} /> All customers</label>
        <label style={{ display: "inline-flex", alignItems: "center", gap: 6 }}><input type="radio" checked={!all} onChange={() => setAll(false)} /> Selected customers ({ids.length})</label>
      </div>
      {!all && (
        <>
          <input className="portal-input-inline" style={smallInput} value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="Filter customers…" />
          <div style={{ maxHeight: 260, overflowY: "auto", border: "1px solid var(--portal-border)", borderRadius: 8, padding: 6, display: "grid", gap: 2 }}>
            {filtered.slice(0, 300).map((c) => (
              <label key={c.customerId} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: "0.85rem", padding: "3px 6px" }}>
                <input type="checkbox" checked={ids.includes(c.customerId)} onChange={(e) => setIds((s) => (e.target.checked ? [...s, c.customerId] : s.filter((x) => x !== c.customerId)))} />
                <span>{c.customerName}{c.enabled ? "" : " (disabled)"}</span>
                <span style={{ color: "var(--portal-muted)" }}>· {c.managerName}</span>
              </label>
            ))}
            {filtered.length > 300 && <div style={{ color: "var(--portal-muted)", fontSize: "0.8rem", padding: 6 }}>Refine the filter to see more.</div>}
          </div>
        </>
      )}
      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
        <button type="button" className="btn btn-primary" onClick={() => void save()} disabled={saving}>{saving ? "Saving…" : "Save assignment"}</button>
        <Status error={error} success={success} />
      </div>
    </section>
  );
}

// ---- inputs -----------------------------------------------------------------------

function InputsSection({ definition, usedInputKeys, onChange }: { definition: ReportDefinition; usedInputKeys: Set<string>; onChange: (fn: (d: ReportDefinition) => ReportDefinition) => void }) {
  const update = (key: string, patch: Partial<ReportInput>) => onChange((d) => ({ ...d, inputs: d.inputs.map((i) => (i.key === key ? { ...i, ...patch } : i)) }));
  const remove = (key: string) => onChange((d) => ({ ...d, inputs: d.inputs.filter((i) => i.key !== key) }));
  const move = (key: string, dir: -1 | 1) =>
    onChange((d) => {
      const i = d.inputs.findIndex((x) => x.key === key);
      const j = i + dir;
      if (i < 0 || j < 0 || j >= d.inputs.length) return d;
      const next = [...d.inputs];
      [next[i], next[j]] = [next[j], next[i]];
      return { ...d, inputs: next };
    });

  return (
    <section className="tile" style={{ display: "grid", gap: 12 }}>
      <header className="tile-head"><h3>Inputs</h3></header>
      {definition.inputs.length === 0 ? (
        <div style={{ color: "var(--portal-muted)", fontSize: "0.9rem" }}>Inputs appear here when a section parameter uses the source “User input”.</div>
      ) : (
        <div style={{ overflowX: "auto" }}>
          <table style={{ borderCollapse: "collapse", width: "100%", fontSize: "0.85rem" }}>
            <thead>
              <tr>
                <th style={thStyle}>Key</th>
                <th style={{ ...thStyle, minWidth: 160 }}>Label</th>
                <th style={thStyle}>Type</th>
                <th style={thStyle}>Required</th>
                <th style={{ ...thStyle, minWidth: 140 }}>Default</th>
                <th style={thStyle} title="Date inputs: interpret in the customer time zone, send UTC">Local → UTC</th>
                <th style={{ ...thStyle, minWidth: 260 }}>Options (select)</th>
                <th style={thStyle} aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {definition.inputs.map((i, idx) => {
                const unused = !usedInputKeys.has(i.key.toLowerCase());
                return (
                  <tr key={i.key} style={unused ? { background: "#fff8e6" } : undefined}>
                    <td style={{ ...tdStyle, fontFamily: "monospace", whiteSpace: "nowrap" }}>{i.key}{unused && <div style={{ color: "#8a5a00", fontSize: "0.72rem" }}>unused</div>}</td>
                    <td style={tdStyle}><input className="portal-input-inline" style={smallInput} value={i.label ?? ""} onChange={(e) => update(i.key, { label: e.target.value })} /><input className="portal-input-inline" style={{ ...smallInput, marginTop: 4 }} value={i.help ?? ""} onChange={(e) => update(i.key, { help: e.target.value })} placeholder="Help text" /></td>
                    <td style={tdStyle}>
                      <select className="portal-input-inline" style={smallInput} value={i.type} onChange={(e) => update(i.key, { type: e.target.value as ReportInput["type"] })}>
                        <option value="text">Text</option>
                        <option value="number">Number</option>
                        <option value="date">Date</option>
                        <option value="datetime">Date & time</option>
                        <option value="bool">Yes / No</option>
                        <option value="select">Choice list</option>
                      </select>
                    </td>
                    <td style={{ ...tdStyle, textAlign: "center" }}><input type="checkbox" checked={!!i.required} onChange={(e) => update(i.key, { required: e.target.checked })} /></td>
                    <td style={tdStyle}><input className="portal-input-inline" style={smallInput} value={i.default ?? ""} onChange={(e) => update(i.key, { default: e.target.value })} placeholder={i.type === "date" || i.type === "datetime" ? "today, today-7, month, month+1, now" : ""} /></td>
                    <td style={{ ...tdStyle, textAlign: "center" }}>{(i.type === "date" || i.type === "datetime") && <input type="checkbox" checked={i.localToUtc !== false} onChange={(e) => update(i.key, { localToUtc: e.target.checked })} />}</td>
                    <td style={tdStyle}>{i.type === "select" && <OptionsEditor input={i} onChange={(patch) => update(i.key, patch)} />}</td>
                    <td style={{ ...tdStyle, whiteSpace: "nowrap" }}>
                      <button type="button" className="btn btn-secondary" onClick={() => move(i.key, -1)} disabled={idx === 0} style={{ padding: "2px 8px" }} aria-label="Move up">↑</button>{" "}
                      <button type="button" className="btn btn-secondary" onClick={() => move(i.key, 1)} disabled={idx === definition.inputs.length - 1} style={{ padding: "2px 8px" }} aria-label="Move down">↓</button>{" "}
                      <button type="button" className="btn btn-outline-danger" onClick={() => remove(i.key)} disabled={!unused} title={unused ? "Remove" : "In use by a section"} style={{ padding: "2px 8px" }} aria-label="Remove"><Trash2 size={12} /></button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

/** Fixed value/label options ("0 = All providers" per line) or a procedure returning value/label rows. */
function OptionsEditor({ input, onChange }: { input: ReportInput; onChange: (patch: Partial<ReportInput>) => void }) {
  const text = (input.options ?? []).map((o) => (o.label && o.label !== o.value ? `${o.value} = ${o.label}` : o.value)).join("\n");
  const [draft, setDraft] = useState(text);
  useEffect(() => setDraft(text), [text]);
  const commit = () => {
    const options: ReportOption[] = draft
      .split(/\r?\n/)
      .map((l) => l.trim())
      .filter(Boolean)
      .map((l) => {
        const m = /^(.*?)\s*=\s*(.*)$/.exec(l);
        return m ? { value: m[1].trim(), label: m[2].trim() } : { value: l, label: l };
      });
    onChange({ options });
  };
  return (
    <div style={{ display: "grid", gap: 4 }}>
      <textarea className="portal-input-inline" style={{ ...smallInput, minHeight: 56, fontFamily: "monospace" }} value={draft} onChange={(e) => setDraft(e.target.value)} onBlur={commit} placeholder={"0 = All providers\n1 = SIP trunks only"} />
      <input className="portal-input-inline" style={smallInput} value={input.optionsProcedure ?? ""} onChange={(e) => onChange({ optionsProcedure: e.target.value || undefined })} placeholder="…or procedure returning value, label (spPortalReport_*)" />
    </div>
  );
}

// ---- preview ------------------------------------------------------------------------

function PreviewSection({ reportName, definition, audience, dirty }: { reportName: string; definition: ReportDefinition; audience: ReportAudience; dirty: boolean }) {
  const [open, setOpen] = useState(false);
  const [customerId, setCustomerId] = useState("");
  const [managerId, setManagerId] = useState("");
  const [runKey, setRunKey] = useState(0);

  return (
    <section className="tile" style={{ display: "grid", gap: 12 }}>
      <header className="tile-head" style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
        <h3>Preview</h3>
        {audience === "customer" && (
          <input className="portal-input-inline" style={{ ...smallInput, width: 140 }} value={customerId} onChange={(e) => setCustomerId(e.target.value)} placeholder="Customer id" />
        )}
        {audience === "manager" && (
          <input className="portal-input-inline" style={{ ...smallInput, width: 140 }} value={managerId} onChange={(e) => setManagerId(e.target.value)} placeholder="Manager id" />
        )}
        <button type="button" className="btn btn-secondary" onClick={() => { setOpen(true); setRunKey((k) => k + 1); }} disabled={definition.sections.length === 0} style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
          <Eye size={14} /> {open ? "Reload preview" : "Show preview"}
        </button>
        {dirty && <span style={{ fontSize: "0.8rem", color: "var(--portal-muted)" }}>Preview uses the unsaved definition.</span>}
      </header>
      {open && (
        <DynamicReport
          key={runKey}
          definition={{ ...definition, inputs: definition.inputs.map((i) => ({ ...i })) }}
          run={async (inputs) =>
            (await portalReportsAdminApi.preview(reportName, { inputs, customerId: customerId ? Number(customerId) : null, managerId: managerId ? Number(managerId) : null, definition })).sections
          }
          loadOptions={async (key) => {
            const input = definition.inputs.find((i) => i.key === key);
            if (!input?.optionsProcedure) return input?.options ?? [];
            const res = await portalReportsAdminApi.testRun(input.optionsProcedure, { values: {}, customerId: customerId ? Number(customerId) : null, managerId: managerId ? Number(managerId) : null });
            const vi = Math.max(0, res.columns.findIndex((c) => c.name.toLowerCase() === "value"));
            const li = res.columns.findIndex((c) => c.name.toLowerCase() === "label");
            return res.rows.map((r) => ({ value: String(r[vi] ?? ""), label: String(r[li >= 0 ? li : vi] ?? "") }));
          }}
          exportName="preview"
        />
      )}
    </section>
  );
}
